/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import { Alert, Box, Button, Grid, LinearProgress, Paper, Snackbar, SnackbarCloseReason } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { VTextField,  VForm, useVForm, IVFormErrors, SelectNumber, AutoCompleteProdutor, AutoCompleteFazenda, VTextFieldData, VFieldMoney, VTextFieldCpf, cpfValidator, cnpjValidator, AutoCompleteParceiro} from '../../../shared/forms';
import { FerramentasDeDetalhe } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { LCDPRService } from '../../../shared/services/api/fiscal/LCDPR/LCDPRService';
import { AutoCompleteContaBancaria } from '../../../shared/forms/AutoCompleteContaBancaria';

interface IFormData {
  idprodutor: string;
  idfazenda: string;
  idconta: string;
  datacaixa: Date;
  dataemissao: Date;
  numero: string;
  tipodoc: number;
  tipolan: number;
  idparceiro?: string | null;
  entrada: number;
  saida: number;
  historico: string;
  idparticipante: string;
}

const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  idprodutor: yup.string().required(),
  idfazenda: yup.string().required(),
  idconta: yup.string().required(),
  datacaixa: yup.date().required(),
  dataemissao: yup.date().required(),
  numero: yup.string().required(),
  tipodoc: yup.number().required(),
  tipolan: yup.number().required(),
  idparceiro: yup.string().nullable().optional().default(null),
  entrada: yup.number().optional().default(0).when('tipolan', {
    is: 1 || 3,
    then: () => yup.number().required('Campo obrigatório').moreThan(0),
  }
  ).when('tipolan', {
    is: 2,
    then: () => yup.number().max(0, 'Entrada não pode ser maior que zero quando o tipo de Lançamento for Despesa')
  }
  ),
  saida: yup.number().optional().default(0).when('tipolan', {
    is: 2,
    then: () => yup.number().required('Campo obrigatório').moreThan(0),
  }).when('tipolan', {
    is: 1 || 3,
    then: () => yup.number().max(0, 'Saída não pode ser maior que zero quando o tipo de Lançamento for Receita')
  }
  ),
  historico: yup.string().required(),
  idparticipante: yup.string().optional().default('').when('idparceiro', {
    is: null,
    then: () => yup.string().required('Campo obrigatório')
      .test('cpf-ou-cnpj', 'CPF ou CNPJ inválido', function(value) {
        if (!value) {
          return this.createError({ path: this.path, message: 'Campo obrigatório' });
        }

        if (value.length === 11) {
          if (!cpfValidator.call(this, value)) {
            return this.createError({ path: this.path, message: 'CPF inválido' });
          }
        } else if (value.length === 14) {
          if (!cnpjValidator.call(this, value)) {
            return this.createError({ path: this.path, message: 'CNPJ inválido' });
          }
        } else {
          return this.createError({ path: this.path, message: 'CPF ou CNPJ inválido' });
        }

        return true;
      })
  }),
});

export const DetalheDeLCDPR: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();
  const { formRef, save, saveAndClose, isSaveAndClose } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const [tipolan, setTipoLan] = useState(1);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error'  | 'info'  | 'success'>('warning');  
          
  const [openConfirm, setOpenConfirm] = useState(false);
            
  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event, 
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if(tms === 'success'){ 
      navigate('/lcdpr');
    }
  };
          
  const handleDelete = () => {
    setOpenConfirm(true); 
  };
          
  const handleConfirmDelete = () => {
    LCDPRService.deleteById(id)
      .then(result => {
        if (result instanceof Error) {
          setOpenConfirm(false);
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setOpenConfirm(false);
          setTms('success');
          setMsg('Registro apagado com sucesso!');
          setOpenmsg(true);
        }
      });
  };
          
  const handleCancelDelete = () => {
    setOpenConfirm(false);
  };

  useEffect(() => {
    if (id !== 'novo') {
      setIsLoading(true);
      LCDPRService.getById(id)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            alert(result.message);
            navigate('/lcdpr');
          } else {
            setTipoLan(result.tipolan); 
            formRef.current?.setData(result);
          } 
        });
    } else {
      formRef.current?.setData({
        datacaixa: new Date(),
        dataemissao: new Date(),
        numero: '',
        tipodoc: 1,
        tipolan: 1,
        entrada: 0,
        saida: 0,
        historico: '',
      });
    }
  }, [id]);
 
  const handleSave = (dados: IFormData) => {
    formValidationSchema.
      validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        if (id === 'novo') {
          LCDPRService
            .create(dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Lançamento Realizado com sucesso!');
                setOpenmsg(true);
              }
            });
        } else {
          LCDPRService
            .updateById(id, dadosValidados )
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Alteração concluída com sucesso!');
                setOpenmsg(true);
              }
            });
        }
      })
      .catch((errors: yup.ValidationError) => {
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;

          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  const handleIdTipoLan = (valor: string | undefined) => {
    if (valor != null) {
      setTipoLan(Number(valor));
    }
  };

  return (
    <LayoutBaseDePagina
      titulo='Lançamento LCDPR'
      barraDeFerramentas={
        <FerramentasDeDetalhe
          textoBotaoNovo='Novo'
          mostrarBotaoSalvar
          mostrarBotaoSalvarEFechar={false}
          mostrarBotaoSalvarCarregando = {isLoading}
          mostrarBotaoSalvarEFecharCarregando = {isLoading}
          mostrarBotaoNovo={id !== 'novo'}
          mostrarBotaoApagar={id !== 'novo'}
          mostrarBotaoApagarCarregando = {isLoading}
          mostrarBotaoImpressao = {false}
          aoClicarEmSalvar={save}
          aoClicarEmSalvarEFechar={saveAndClose}
          aoClicarEmVoltar={() => navigate('/lcdpr')}
          mostrarBotaoVoltarCarregando = {isLoading}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate('/lcdpr/detalhe/novo')}
          mostrarBotaoNovoCarregando = {isLoading}
        />
      }
    >

      <VForm ref={formRef} onSubmit={handleSave}>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg}  autoHideDuration={900} onClose={handleCloseMsg}>
          <Alert
            onClose={handleCloseMsg}
            severity={tms}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>;
        <Snackbar open={openConfirm} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert
            severity="warning"  
            variant="filled"
            sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
            action={
              <>
                <Button color="inherit" size="small" onClick={handleConfirmDelete}>
              Sim
                </Button>
                <Button color="inherit" size="small" onClick={handleCancelDelete}>
              Nâo
                </Button>
              </>
            }
          >
            Tem certeza que deseja excluir este registro?
          </Alert>
        </Snackbar>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">

          <Grid container direction="column" padding={2} spacing={2}>

            {isLoading && (
              <Grid item>
                <LinearProgress variant='indeterminate' />
              </Grid>
            )}

            <Grid container item direction="row" spacing={2}>
              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <AutoCompleteProdutor
                  isExternalLoading={isLoading}                  
                />
              </Grid> 
              <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                <AutoCompleteFazenda
                  isExternalLoading={isLoading}
                />
              </Grid> 
              <Grid item xs={12} sm={12} md={6} lg={5} xl={5}>
                <AutoCompleteContaBancaria
                  name='idconta'
                  isExternalLoading={isLoading}
                  label='Conta Bancária'
                />
              </Grid> 
              <Grid item xs={6} sm={12} md={3} lg={2} xl={2}>
                <VTextFieldData
                  fullWidth
                  name='datacaixa'
                  label='Data Caixa'
                  disabled={isLoading}
                />
              </Grid> 
              <Grid item xs={6} sm={12} md={3} lg={2} xl={2}>
                <VTextFieldData
                  fullWidth
                  size='small'
                  name='dataemissao'
                  label='Data Emissão'
                  disabled={isLoading}
                />
              </Grid> 
              <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                <VTextField
                  fullWidth
                  size='small'
                  name='numero'
                  disabled={isLoading}
                  label='Nº do Documento'
                />
              </Grid> 
              <Grid item xs={12} sm={12} md={3} lg={2.5} xl={2.5}>
                <SelectNumber
                  name='tipodoc'
                  label='Tipo de Documento'
                  data={[
                    { nameBD: 1, descricao: 'Nota Fiscal' },
                    { nameBD: 2, descricao: 'Fatura' },
                    { nameBD: 3, descricao: 'Recibo' },
                    { nameBD: 4, descricao: 'Contrato' },
                    { nameBD: 5, descricao: 'Folha de Pagamento' },
                    { nameBD: 6, descricao: 'Outros' },
                  ]}
                  
                  disabled={isLoading}
                />  
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={2.5} xl={2.5}>
                <SelectNumber
                  name='tipolan'
                  label='Tipo de Lançamento'
                  data={[
                    { nameBD: 1, descricao: 'Receita da Atividade Rural' },
                    { nameBD: 2, descricao: 'Despesas de Custeio e Investimentos' },
                    { nameBD: 3, descricao: 'Receita de Produtos Entregues no Ano' },
                  ]}
                  valor={handleIdTipoLan}
                  disabled={isLoading}
                />  
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
                <AutoCompleteParceiro
                  isExternalLoading={isLoading}
                />  
              </Grid> 
              <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                <VTextFieldCpf
                  fullWidth
                  size='small'
                  name='idparticipante'
                  disabled={isLoading}
                  label='CPF/CNPJ de Parceiro não Cadastrado'
                />
              </Grid> 
              <Grid item xs={12} sm={12} md={6} lg={2.5} xl={2.5}>
                <VFieldMoney
                  fullWidth
                  size='small'
                  name='entrada'
                  disabled={tipolan === 2 ? true : isLoading}
                  label='Valor Entrada'
                />
              </Grid> 
              <Grid item xs={12} sm={12} md={6} lg={2.5} xl={2.5}>
                <VFieldMoney
                  fullWidth
                  size='small'
                  name='saida'
                  disabled={tipolan === 2 ? isLoading : true}
                  label='Valor Saída'
                />
              </Grid> 
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <VTextField
                  fullWidth
                  name='historico'
                  label='Histórico'
                  disabled={isLoading}
                />
              </Grid>    
            </Grid>
          </Grid>
        </Box>
      </VForm> 

    </LayoutBaseDePagina>
  );
};